import React from "react";
import { Link } from "react-router-dom";
import LegalPage from "../components/LegalPage";
import usePageTitle from "../hooks/usePageTitle";

/**
 * Пользовательское соглашение Рублокса.
 *
 * Рублокс — бесплатная платформа, поэтому это не возмездная публичная оферта
 * (как у minecraftia с подпиской 2990₽), а соглашение об условиях бесплатного
 * использования: предмет, правила, ответственность за контент, права на
 * интеллектуальную собственность, статус алмазов.
 *
 * Юрлицо — ИП Иванкова В.С. (единая экосистема с онлайн-школой «Майнкрафтия»).
 */
export default function TermsPage() {
  usePageTitle("Пользовательское соглашение");
  return (
    <LegalPage
      title="Пользовательское соглашение"
      subtitle="Правила пользования игровой платформой «Рублокс». Редакция от 15 мая 2026 года."
      active="terms"
    >
      <p>
        <em>Редакция от 15 мая 2026 года</em>
      </p>

      <p>
        Настоящее Пользовательское соглашение (далее — Соглашение) регулирует
        отношения между Индивидуальным предпринимателем Иванковой В.С. (далее —
        Оператор) и пользователем игровой платформы «Рублокс» (далее —
        Платформа). Платформа расположена по адресу{" "}
        <a href="https://rublox.pro">https://rublox.pro</a> и доступна также в
        виде приложений для Android и Windows.
      </p>

      <h2>1. Термины и определения</h2>
      <ul>
        <li>
          <b>Оператор:</b> Индивидуальный предприниматель Иванкова Виктория
          Сергеевна, ОГРНИП 322237500039230, ИНН 233507286445.
        </li>
        <li>
          <b>Платформа:</b> игровая платформа «Рублокс» — сайт{" "}
          <a href="https://rublox.pro">https://rublox.pro</a> и приложения для
          запуска и публикации 3D-игр. Платформа является частью экосистемы
          онлайн-школы программирования и математики «Майнкрафтия».
        </li>
        <li>
          <b>Пользователь:</b> совершеннолетнее лицо (18 лет и старше),
          совершившее регистрацию на Платформе как законный представитель
          несовершеннолетнего.
        </li>
        <li>
          <b>Обучающийся:</b> несовершеннолетнее лицо (ребёнок), которому
          Пользователь (законный представитель) предоставляет доступ к
          Платформе.
        </li>
        <li>
          <b>Контент:</b> игры, 3D-модели, скрипты, комментарии и иные
          материалы, создаваемые и публикуемые на Платформе.
        </li>
        <li>
          <b>Алмазы:</b> внутренняя игровая валюта Платформы.
        </li>
      </ul>

      <h2>2. Важное условие о возрасте</h2>
      <p>
        2.1. Основная аудитория Платформы — дети в возрасте от 8 до 16 лет. В
        соответствии с законодательством Российской Федерации (п. 1 ст. 26 и
        п. 1 ст. 28 ГК РФ) регистрация Обучающегося осуществляется{" "}
        <b>законным представителем</b> несовершеннолетнего (родителем,
        усыновителем, опекуном или попечителем).
      </p>
      <p>
        2.2. Совершая регистрацию, лицо подтверждает, что ему исполнилось 18 лет
        и что оно является законным представителем Обучающегося. Именно законный
        представитель является Пользователем для целей настоящего Соглашения и
        несёт ответственность за использование Платформы Обучающимся.
      </p>

      <h2>3. Предмет Соглашения</h2>
      <p>
        3.1. Оператор предоставляет Пользователю (Обучающемуся){" "}
        <b>бесплатный</b> доступ к функционалу Платформы: запуску опубликованных
        3D-игр через приложения, публикации собственных игр, созданных в
        3D-редакторе Рублокс, участию в рейтингах и использованию иных
        сервисов Платформы.
      </p>
      <p>
        3.2. Платформа предоставляется бесплатно. Оператор не взимает плату за
        регистрацию, доступ к играм, публикацию игр или использование
        приложений. Внутри игр Платформы отсутствуют покупки за реальные
        денежные средства.
      </p>
      <p>
        3.3. Игры Платформы запускаются только через приложения для Android и
        Windows. Сайт <a href="https://rublox.pro">rublox.pro</a> является
        витриной — запуск игр в браузере не предусмотрен.
      </p>
      <p>
        3.4. Создание игр осуществляется в 3D-редакторе Рублокс, доступном на
        сайте онлайн-школы «Майнкрафтия».
      </p>

      <h2>4. Регистрация и учётная запись</h2>
      <p>
        4.1. Для доступа к полному функционалу Платформы необходима регистрация.
        Аккаунт Рублокса — <b>единый</b> с онлайн-школой «Майнкрафтия»:
        регистрация на одном ресурсе создаёт учётную запись для обоих.
      </p>
      <p>
        4.2. При регистрации Пользователь обязуется предоставить достоверные
        данные, включая действующий адрес электронной почты.
      </p>
      <p>
        4.3. Пользователь обязан обеспечивать конфиденциальность своих учётных
        данных и несёт ответственность за все действия, совершённые под его
        учётной записью.
      </p>
      <p>
        4.4. Регистрируясь на Платформе, Пользователь подтверждает, что
        ознакомился и согласен с настоящим Соглашением, а также с{" "}
        <Link to="/privacy-policy">
          Политикой обработки персональных данных
        </Link>
        .
      </p>

      <h2>5. Правовой статус — Платформа в стадии Альфа-версии</h2>
      <p>
        5.1. Платформа «Рублокс» находится в стадии активной разработки
        (Альфа-версия). Платформа работоспособна, но может содержать ошибки,
        недоработки и нестабильно работающие функции.
      </p>
      <p>
        5.2. Оператор постоянно дорабатывает Платформу. Состав, содержание и
        работоспособность функций могут изменяться без предварительного
        уведомления.
      </p>
      <p>
        5.3. Пользователь использует Платформу «как есть» (as is). Оператор не
        гарантирует бесперебойной и безошибочной работы Платформы.
      </p>

      <h2>6. Права и обязанности сторон</h2>
      <p>
        6.1. <b>Обязанности Оператора:</b>
      </p>
      <ul>
        <li>
          предоставлять Пользователю (Обучающемуся) доступ к Платформе на
          условиях настоящего Соглашения;
        </li>
        <li>
          обеспечивать сохранность персональных данных в соответствии с
          законодательством Российской Федерации;
        </li>
        <li>
          оказывать техническую поддержку в объёме, предусмотренном на
          Платформе.
        </li>
      </ul>
      <p>
        6.2. <b>Права Оператора:</b>
      </p>
      <ul>
        <li>
          приостанавливать или прекращать доступ Пользователя (Обучающегося) к
          Платформе при нарушении условий настоящего Соглашения;
        </li>
        <li>
          проводить технические работы, на время которых доступ к Платформе
          может быть ограничен;
        </li>
        <li>изменять состав и содержание Платформы без уведомления;</li>
        <li>
          модерировать публикуемый Контент (игры, комментарии, баг-репорты) и
          удалять материалы, нарушающие законодательство или правила Платформы.
        </li>
      </ul>
      <p>
        6.3. <b>Обязанности Пользователя:</b>
      </p>
      <ul>
        <li>предоставить достоверные данные при регистрации;</li>
        <li>обеспечивать конфиденциальность своих учётных данных;</li>
        <li>
          соблюдать настоящее Соглашение и требования законодательства
          Российской Федерации;
        </li>
        <li>
          как законный представитель — контролировать использование Платформы
          несовершеннолетним Обучающимся;
        </li>
        <li>
          не использовать Платформу для распространения противоправного,
          оскорбительного или вредоносного Контента.
        </li>
      </ul>

      <h2>7. Пользовательский контент</h2>
      <p>
        7.1. Пользователь несёт полную ответственность за любой Контент (игры,
        модели, скрипты, комментарии), размещённый им самим или Обучающимся на
        Платформе.
      </p>
      <p>
        7.2. Запрещается публиковать Контент, который нарушает законодательство
        Российской Федерации, права третьих лиц, содержит вредоносный код,
        оскорбления, насилие или иные недопустимые материалы.
      </p>
      <p>
        7.3. Публикуя игру или иной Контент на Платформе, Пользователь
        предоставляет Оператору неисключительную бессрочную лицензию на его
        отображение, хранение и продвижение в рамках Платформы. Авторские права
        на Контент остаются у Пользователя (Обучающегося).
      </p>
      <p>
        7.4. Оператор вправе удалить любой Контент, нарушающий настоящее
        Соглашение или законодательство, без предварительного уведомления.
      </p>

      <h2>8. Интеллектуальная собственность</h2>
      <p>
        8.1. Все материалы Платформы, включая программный код, дизайн,
        графику, логотипы и иные объекты (за исключением пользовательского
        Контента), являются объектами интеллектуальной собственности Оператора
        или третьих лиц, предоставивших Оператору права на их использование.
      </p>
      <p>
        8.2. Пользователю предоставляется ограниченная, неисключительная,
        непередаваемая лицензия на использование Платформы исключительно в
        личных некоммерческих целях.
      </p>
      <p>
        8.3. Запрещается копирование, распространение и коммерческое
        использование материалов Платформы без письменного согласия Оператора.
      </p>

      <h2>9. Внутренняя валюта «Алмазы»</h2>
      <p>
        9.1. «Алмазы» — внутренняя игровая валюта Платформы. Алмазы начисляются
        за активность в экосистеме (в том числе за решение учебных задач в
        онлайн-школе «Майнкрафтия») и используются для приобретения внутриигровых
        предметов — скинов и иных элементов оформления.
      </p>
      <p>
        9.2. Алмазы <b>не являются</b> электронными денежными средствами,{" "}
        <b>не подлежат</b> обмену на реальные денежные средства, выводу или
        возврату. Алмазы имеют исключительно игровое назначение внутри Платформы.
      </p>
      <p>
        9.3. Оператор вправе изменять правила начисления и расходования алмазов,
        а также стоимость внутриигровых предметов.
      </p>

      <h2>10. Ответственность сторон</h2>
      <p>
        10.1. Стороны несут ответственность в соответствии с законодательством
        Российской Федерации.
      </p>
      <p>
        10.2. Оператор не несёт ответственности за невозможность пользования
        Платформой, вызванную обстоятельствами вне его разумного контроля,
        включая действия третьих лиц (интернет-провайдеров, хостинг-провайдеров),
        сбои в работе оборудования Пользователя, действие непреодолимой силы.
      </p>
      <p>
        10.3. Поскольку Платформа предоставляется бесплатно и в стадии
        Альфа-версии, Оператор не несёт ответственности за возможную потерю
        пользовательского Контента, перерывы в работе или ошибки Платформы.
        Оператор прилагает разумные усилия для сохранности данных, в том числе
        выполняет регулярное резервное копирование.
      </p>
      <p>
        10.4. Оператор не несёт ответственности за Контент, создаваемый
        пользователями Платформы.
      </p>

      <h2>11. Срок действия и расторжение</h2>
      <p>
        11.1. Соглашение вступает в силу с момента регистрации Пользователя на
        Платформе и действует бессрочно — до удаления учётной записи.
      </p>
      <p>
        11.2. Пользователь вправе в любой момент прекратить использование
        Платформы и запросить удаление учётной записи, направив заявление на
        e-mail <b>MaksimIvankov26@yandex.ru</b>.
      </p>
      <p>
        11.3. Оператор вправе прекратить доступ Пользователя (Обучающегося) к
        Платформе в случае существенного нарушения условий настоящего
        Соглашения.
      </p>

      <h2>12. Разрешение споров</h2>
      <p>
        12.1. Все споры разрешаются путём переговоров. До обращения в суд
        Пользователь обязан направить Оператору письменную претензию на e-mail{" "}
        <b>MaksimIvankov26@yandex.ru</b>; срок рассмотрения — не более 10
        (десяти) рабочих дней.
      </p>
      <p>
        12.2. Жалобы на обработку персональных данных могут быть направлены в
        Роскомнадзор: <a href="https://rkn.gov.ru/">https://rkn.gov.ru/</a>.
      </p>

      <h2>13. Заключительные положения</h2>
      <p>
        13.1. Настоящее Соглашение регулируется законодательством Российской
        Федерации.
      </p>
      <p>
        13.2. Оператор вправе в одностороннем порядке изменять условия
        Соглашения, уведомляя Пользователей путём публикации новой редакции на
        Платформе. Новая редакция вступает в силу с момента её размещения.
      </p>
      <p>
        13.3. Если какое-либо положение Соглашения будет признано
        недействительным, это не влияет на действительность остальных положений.
      </p>

      <h2>14. Реквизиты Оператора</h2>
      <p>
        <b>Индивидуальный предприниматель Иванкова Виктория Сергеевна</b>
      </p>
      <p>ОГРНИП: 322237500039230</p>
      <p>ИНН: 233507286445</p>
      <p>Адрес: Краснодарский край, г. Кореновск, ул. Мира 93</p>
      <p>
        Сайт: <a href="https://rublox.pro">https://rublox.pro</a>
      </p>
      <p>E-mail: MaksimIvankov26@yandex.ru</p>
    </LegalPage>
  );
}
